/**
 * 領収書の分割発行まわりの金額計算（ODR-1671）。
 *
 * バックエンド `ReceiptPapersTable::isValidPrice` と同じ不変条件を共有する純粋関数。
 * 金券・その他割引は「丸ごと」複数の領収書に配分（分割）できるが、過大発行はしない:
 *  (a) お預かり(現金) ＝ 選択商品 − 適用割引(金券＋その他割引) ≧ 0
 *  (b) 残り発行可能 ＝ 残り商品 − 残り割引 ≧ 0（残った金券・割引を後続で使い切れる）
 *
 * ODR-1704: (a) から追加明細を外した。金券・割引は「この会計の商品」でしか相殺できず、
 * 追加明細（別決済済みの外部明細）は金券を吸収できないため。
 *
 * お釣りあり金券（超過分をお釣りで返す金券）は discount_price が額面のまま記録されるため、
 * (a) が「金券が商品を上回る」と誤判定して発行できない。お釣りを (a) に足すと分割発行の
 * たびに二重計上され (b) の過剰発行防止が緩むので、ここでは扱わない。
 * 実効金券（金券 − お釣り）モデルとして別途対応する。
 *
 * DOM や Vue インスタンスに依存しないため、そのままユニットテストできる。
 */

export interface ReceiptCalcInput {
  /** 会計の商品総額（payment.total_price） */
  totalProduct: number;
  /** 会計の割引総額（その他割引 ＋ 金券合計） */
  totalDiscount: number;
  /** 他の領収書で発行済みの商品額 */
  issuedProduct: number;
  /** 他の領収書で発行済みの割引額（金券 ＋ その他割引） */
  issuedDiscount: number;
  /** いま選択している商品額 */
  selectedProduct: number;
  /** いま適用している割引額（金券 ＋ チェック中のその他割引） */
  appliedDiscount: number;
}

export type ReceiptCalcError = "CASH_NEGATIVE" | "REMAINING_NEGATIVE";

export interface ReceiptCalcResult {
  /** 発行後の残り商品額 */
  remainingProduct: number;
  /** 発行後の残り割引額（金券 ＋ その他割引） */
  remainingDiscount: number;
  /** 発行後の残り発行可能額（残り商品 − 残り割引） */
  remainingIssuable: number;
  /** この領収書のお預かり(現金)相当額 */
  cashAmount: number;
  /** 発行不可の理由コード。発行可能なら null */
  error: ReceiptCalcError | null;
}

/** 発行不可コードに対応する表示メッセージ */
export const RECEIPT_CALC_MESSAGES: Record<ReceiptCalcError, string> = {
  CASH_NEGATIVE: "金券・割引が商品金額を上回っています。",
  REMAINING_NEGATIVE:
    "残りの商品では金券・割引を使い切れません。配分を見直してください。",
};

/**
 * 残り・お預かりの各額と、発行不可の理由を算出する。
 *
 * @param input 会計・発行済み・選択中の各額
 * @return 計算結果
 */
export function receiptCalc(input: ReceiptCalcInput): ReceiptCalcResult {
  const remainingProduct =
    input.totalProduct - input.issuedProduct - input.selectedProduct;
  const remainingDiscount =
    input.totalDiscount - input.issuedDiscount - input.appliedDiscount;
  const remainingIssuable = remainingProduct - remainingDiscount;
  // ODR-1704: 追加明細は含めない。金券・割引は「この会計の商品」でしか相殺できず、
  // 追加明細は別決済済みの外部明細なので金券を吸収できないため
  // （旧実装は明細を加算していたので、明細を足すと金券超過の警告が消えてしまっていた）。
  const cashAmount = input.selectedProduct - input.appliedDiscount;

  let error: ReceiptCalcError | null = null;
  if (cashAmount < 0) {
    error = "CASH_NEGATIVE";
  } else if (remainingIssuable < 0) {
    error = "REMAINING_NEGATIVE";
  }

  return {
    remainingProduct,
    remainingDiscount,
    remainingIssuable,
    cashAmount,
    error,
  };
}

/**
 * この領収書が発行可能か（お預かり≧0 かつ 残り発行可能≧0）。
 * バックエンド `isValidPrice` の (a)(b) と同一判定。
 *
 * @param input 会計・発行済み・選択中の各額
 * @return 発行可能なら true
 */
export function isReceiptIssuable(input: ReceiptCalcInput): boolean {
  return receiptCalc(input).error === null;
}

/**
 * ODR-1704 モデルB: 内容が同一の領収書を最大何枚発行できるかを算出する対象項目。
 * 商品・追加明細を統一的に扱う（各項目「手持ち数量」と「1枚あたり必要数」を持つ）。
 */
export interface IssuableNeed {
  /** 手持ち数量（発行済みを除いた残り） */
  available: number;
  /** 1枚あたりに必要な数量 */
  perReceipt: number;
}

/**
 * ODR-1704 モデルB: 商品の「手持ち数量」＝ 数量 − 他の領収書で発行済みの数。
 *
 * 「1枚あたり個数」入力の上限であり、maxIssuableCount の available になる。
 * 旧 isPrinted（ユニット index と発行済み数を比較してユニット行を disable する）の置き換え。
 *
 * @param totalQuantity 会計上の数量
 * @param issuedQuantity 他の領収書で発行済みの数
 * @return 手持ち数量（0未満にはしない）
 */
export function availableQuantity(totalQuantity: number, issuedQuantity: number): number {
  const total = Number(totalQuantity) || 0;
  const issued = Number(issuedQuantity) || 0;

  return Math.max(0, total - issued);
}

/**
 * 選択項目から、内容が同一の領収書を最大何枚発行できるかを返す。
 *
 * 各項目 floor(available / perReceipt) の最小値。1枚あたり必要数が 0 以下の項目
 * （＝この領収書に含めない項目）は制約に含めない。対象項目が無ければ 0。
 *
 * @param needs 発行に必要な各項目の手持ち数量・1枚あたり必要数
 * @return 発行可能な同一領収書の最大枚数
 */
export function maxIssuableCount(needs: IssuableNeed[]): number {
  const active = needs.filter((n) => n.perReceipt > 0);
  if (active.length === 0) {
    return 0;
  }

  return active.reduce(
    (min, n) => Math.min(min, Math.floor(n.available / n.perReceipt)),
    Number.POSITIVE_INFINITY
  );
}
